/**
 * Relation 카드 그리드 — 각 카드는 name/displayName + directionality 표시 + usage 배지.
 *
 * directionality = "directed" 인 경우 name 앞에 → 표기, undirected 는 ↔ 표기.
 * inverseOf 가 있으면 "inverse of {name}" 태그 노출.
 * aliases 가 있으면 카드 하단에 회색 동의어 칩 노출 — 입력 시 canonical 로 정규화됨.
 */
import type { RelationDef } from "../../api/admin-client.ts"
import { RELATION_COLORS } from "../graph/graph-colors.ts"

interface RelationCardGridProps {
	readonly relations: ReadonlyArray<RelationDef>
	readonly usage?: Record<string, number>
	readonly selectedName?: string
	readonly onSelect: (name: string) => void
	readonly onAdd: () => void
	readonly onDelete: (name: string) => void
}

function resolveRelationColor(relation: RelationDef): string {
	return relation.color ?? RELATION_COLORS[relation.name] ?? RELATION_COLORS.default ?? "#4b5563"
}

function directionSymbol(rel: RelationDef): string {
	return rel.directionality === "undirected" ? "↔" : "→"
}

export function RelationCardGrid(props: RelationCardGridProps) {
	return (
		<section className="ontology-section">
			<h3 className="ontology-section-title">
				관계 정의 <span className="muted">({props.relations.length})</span>
			</h3>
			<div className="ontology-card-grid">
				{props.relations.map((relation) => {
					const used = props.usage?.[relation.name] ?? 0
					const isSelected = props.selectedName === relation.name
					const color = resolveRelationColor(relation)
					return (
						<button
							type="button"
							key={relation.name}
							className={`ontology-card${isSelected ? " is-selected" : ""}`}
							onClick={() => props.onSelect(relation.name)}
						>
							<div className="ontology-card-head">
								<span className="ontology-card-arrow" style={{ color, borderColor: color }} aria-hidden>
									{directionSymbol(relation)}
								</span>
								<div className="ontology-card-title">
									<strong>{relation.displayName || relation.name}</strong>
									<code className="inline">{relation.name}</code>
								</div>
								<button
									type="button"
									className="ontology-card-remove"
									aria-label={`${relation.name} 삭제`}
									onClick={(e) => {
										e.stopPropagation()
										props.onDelete(relation.name)
									}}
								>
									×
								</button>
							</div>
							{relation.description && <p className="ontology-card-desc">{relation.description}</p>}
							<div className="ontology-card-foot">
								{relation.inverseOf && (
									<span className="chip" title="역방향 관계">
										inv: {relation.inverseOf}
									</span>
								)}
								<span className={`badge${used > 0 ? " badge-active" : " badge-disabled"}`}>
									{used}회 사용
								</span>
							</div>
							{relation.aliases && relation.aliases.length > 0 && (
								<div className="ontology-card-aliases">
									{relation.aliases.map((alias) => (
										<span
											key={alias}
											className="chip chip-alias"
											title={`이 동의어가 입력되면 ${relation.name}로 정규화됩니다`}
										>
											≈ {alias}
										</span>
									))}
								</div>
							)}
						</button>
					)
				})}

				<button type="button" className="ontology-card ontology-card-add" onClick={props.onAdd}>
					<span className="ontology-card-add-plus" aria-hidden>
						+
					</span>
					<span>관계 추가</span>
				</button>
			</div>
		</section>
	)
}
